import React, { useCallback, useEffect } from 'react';

import ActionCreator from '@components/ActionCreator/ActionCreator';
import BaseModal from '@components/Overlays/Modals/BaseModal/BaseModal';
import { BancontactMobileDetails, Details } from '@hooks/useActionCreator';
import { useTranslation } from '@hooks/useTranslations';
import { useAppContext } from '@providers/AppContextProvider';
import { useConfig } from '@providers/ConfigProvider';
import { OverlayIds, useOverlays } from '@providers/OverlaysProvider';
import { useRetryState } from '@providers/RetryDataProvider';
import { useThunkDispatch } from '@providers/ThunkDispatchProvider';
import { processPaymentAuthorisation } from '@services/retryPaymentService';
import { convertRunningEnvToAdyenEnv } from '@utils/utils';

const BancontactMobileAuthActionModal: React.FC = () => {
  const {
    config: {
      env,
      adyen: { clientKey },
    },
  } = useConfig();
  const { t } = useTranslation();
  const { hideOverlay } = useOverlays();
  const { locale, shopBundle } = useAppContext();
  const {
    retryState: { initiatePaymentData, customerId, processAuthorisationData },
  } = useRetryState();
  const { thunkDispatch } = useThunkDispatch();
  const retryPaymentStatus = processAuthorisationData?.authorizationResult?.retryPaymentStatus;

  const overlayId = OverlayIds.BANCONTACT_MOBILE_AUTH;
  const handlePaymentDetails = useCallback(
    (details: Details) => {
      thunkDispatch(
        processPaymentAuthorisation({
          customerId: String(customerId),
          paymentId: initiatePaymentData?.paymentId,
          paymentMethod: 'BANCONTACT_MOBILE',
          payload: (details as BancontactMobileDetails).payload,
        }),
      );
    },
    [customerId, initiatePaymentData?.paymentId],
  );

  const actionTimedOutHandler = useCallback(() => {
    hideOverlay(overlayId);
  }, [hideOverlay, overlayId]);

  useEffect(() => {
    if (retryPaymentStatus === 'SUCCESSFUL') hideOverlay(overlayId);
  }, [hideOverlay, overlayId, retryPaymentStatus]);

  return initiatePaymentData?.actionObject ? (
    <BaseModal
      modalId={overlayId}
      heading={t('mzp.order.detail.retry.payment.qrcode.info')}
      headingId="bankcontact-mobile-modal"
      closeBtnAriaLabel="Close"
      dataZta="modal-bankContantMobile"
      size="md">
      <ActionCreator
        paymentAction={initiatePaymentData?.actionObject}
        clientKey={clientKey}
        adyenEnvironment={convertRunningEnvToAdyenEnv(env)}
        locale={locale}
        shopBundle={shopBundle}
        sendPaymentDetailsHandler={handlePaymentDetails}
        actionTimedOutHandler={actionTimedOutHandler}
        invalidQRCodeActionShopUrl={'#'}
      />
    </BaseModal>
  ) : null;
};

export default BancontactMobileAuthActionModal;
